/** RecipeV2 "Trial" tab (Demo Mode only, local-only - no backend persistence yet).
 * Covers the "chef builds product" commercial planning story: expected demand, labour
 * needed, forecast P&L, and trial sites/dates/success criteria. Includes a "Refer to
 * Procurement" action that, when the product needs a new supplier, writes a pending
 * supplier referral (see DemoScenarioContext) picked up by the Suppliers list/form -
 * the one deliberate cross-screen link in the demo. See
 * `src/mocks/data/productPlanningData.ts`. */

import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  MenuItem,
  Select,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { Section } from '../../../components/ui/Section';
import { applyRoundAndFormat } from '../../../utils';
import { RecipeV2Detail } from '../types';
import { getProductPlanningProfile, TrialDecision, TrialOutcome, TrialStatus } from '../../../mocks/data/productPlanningData';
import { useDemoScenario } from '../../../contexts/DemoScenarioContext';
import { UserContext } from '../../../components/shared/useUser';

interface TrialTabProps {
  recipe: RecipeV2Detail;
  currencyCode?: string;
}

/** The full trial lifecycle this tab walks through, in order - see productPlanningData.ts
 * for where each step's data lives. */
const LIFECYCLE_STEPS = [
  'createTrialItem',
  'selectSites',
  'setDates',
  'defineCriteria',
  'approve',
  'runTrial',
  'monitor',
  'review',
  'decide',
] as const;

const TrialTab = ({ recipe, currencyCode }: TrialTabProps) => {
  const { t } = useTranslation('recipeV2');
  const { referrals, addReferral } = useDemoScenario();
  const { hasPermissionTo } = useContext(UserContext);
  const canApprove = hasPermissionTo(['RecipeV2Approve']);
  const [referred, setReferred] = useState(false);

  const profile = getProductPlanningProfile(recipe.id);

  // Trial results capture - local-only (no backend persistence yet), lets the chef/manager
  // record what actually happened during the trial so it can be checked against the
  // forecast P&L and success criteria set out above.
  const [criteriaMet, setCriteriaMet] = useState<boolean[]>(() => profile ? profile.trial.successCriteria.map(() => false) : []);
  const [approved, setApproved] = useState(profile?.trial.approved ?? false);
  const [actualUnitsSoldPerWeek, setActualUnitsSoldPerWeek] = useState(profile?.trial.actualUnitsSoldPerWeek?.toString() ?? '');
  const [actualMarginPercentage, setActualMarginPercentage] = useState(profile?.trial.actualMarginPercentage?.toString() ?? '');
  const [actualWastePercentage, setActualWastePercentage] = useState(profile?.trial.actualWastePercentage?.toString() ?? '');
  const [feedbackNotes, setFeedbackNotes] = useState(profile?.trial.feedbackNotes ?? '');
  const [supplierPerformanceNotes, setSupplierPerformanceNotes] = useState(profile?.trial.supplierPerformanceNotes ?? '');
  const [outcome, setOutcome] = useState<TrialOutcome>(profile?.trial.outcome ?? 'Pending');
  const [resultsSaved, setResultsSaved] = useState(false);
  const [decision, setDecision] = useState<TrialDecision | ''>(profile?.trial.decision ?? '');
  const [decisionNotes, setDecisionNotes] = useState(profile?.trial.decisionNotes ?? '');
  const [decisionSaved, setDecisionSaved] = useState(false);
  const [trialStatus, setTrialStatus] = useState<TrialStatus>(profile?.trial.status ?? 'Draft');

  if (!profile) {
    return (
      <Alert severity="info">
        {t('trial.noPlanningData', 'No commercial planning data captured for this recipe yet.')}
      </Alert>
    );
  }

  const alreadyReferred = referred || referrals.some((referral) => referral.requestedByProduct === profile.productName);

  const handleReferToProcurement = () => {
    if (!profile.proposedSupplierName) return;
    addReferral({
      supplierName: profile.proposedSupplierName,
      requestedByProduct: profile.productName,
      reason: t('trial.referralReason', 'New product requires a new supplier before trial can start.'),
    });
    setReferred(true);
  };

  const handleToggleCriterion = (index: number) => {
    setCriteriaMet((prev) => prev.map((met, i) => (i === index ? !met : met)));
    setResultsSaved(false);
  };

  const marginVariance = actualMarginPercentage !== ''
    ? Number(actualMarginPercentage) - profile.forecastPnL.marginPercentage
    : null;

  const handleSaveResults = () => {
    // lazy: client-side only, no backend persistence yet - see productPlanningData.ts.
    setResultsSaved(true);
  };

  const handleSaveDecision = () => {
    // lazy: client-side only, no backend persistence yet - see productPlanningData.ts.
    setDecisionSaved(true);
  };

  // Active step drives the Stepper - the first lifecycle stage not yet completed.
  const activeStep = [
    true, // create trial item - implicit, the recipe already exists
    profile.trial.trialSites.length > 0,
    Boolean(profile.trial.startDate && profile.trial.endDate),
    profile.trial.successCriteria.length > 0,
    approved,
    approved, // "run trial" starts once approved
    resultsSaved,
    resultsSaved,
    decisionSaved,
  ].findIndex((complete) => !complete);


  return (
    <Stack spacing={5}>
      <Section>
        <Box sx={{ p: 4 }}>
          <Stepper activeStep={activeStep === -1 ? LIFECYCLE_STEPS.length : activeStep}
            alternativeLabel>
            {LIFECYCLE_STEPS.map((step) => (
              <Step key={step}>
                <StepLabel>{t(`trial.lifecycle.${step}`, step)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      </Section>

      {recipe.trialSuccessCriteria && (
        <Section>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 2 }}>
              {t('trial.successCriteriaTitle', 'Recipe development success criteria')}
            </Typography>
            <Grid container
              spacing={2}>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.minimumCustomerRating', 'Min. customer rating')}</Typography>
                <Typography variant="textSM">{recipe.trialSuccessCriteria.minimumCustomerRating.toFixed(1)}/5</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.minimumSalesPer100Diners', 'Min. sales per 100 diners')}</Typography>
                <Typography variant="textSM">{recipe.trialSuccessCriteria.minimumSalesPer100Diners}</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.maximumWastePercentage', 'Max. waste %')}</Typography>
                <Typography variant="textSM">{recipe.trialSuccessCriteria.maximumWastePercentage}%</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.maximumFoodCostPercentage', 'Max. food cost %')}</Typography>
                <Typography variant="textSM">{recipe.trialSuccessCriteria.maximumFoodCostPercentage}%</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.minimumGrossProfitPerPortion', 'Min. GP per portion')}</Typography>
                <Typography variant="textSM">£{recipe.trialSuccessCriteria.minimumGrossProfitPerPortion.toFixed(2)}</Typography>
              </Grid>
              <Grid item
                xs={6}
                md={3}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.minimumOperationalApprovalPercentage', 'Min. operational approval %')}</Typography>
                <Typography variant="textSM">{recipe.trialSuccessCriteria.minimumOperationalApprovalPercentage}%</Typography>
              </Grid>
              <Grid item
                xs={12}
                md={6}>
                <Typography variant="textXS"
                  color="text.secondary">{t('trial.unresolvedAllergenConcernsAllowed', 'Unresolved allergen concerns allowed')}</Typography>
                <Typography variant="textSM">{recipe.trialSuccessCriteria.unresolvedAllergenConcernsAllowed ? t('common.yes', 'Yes') : t('common.no', 'No')}</Typography>
              </Grid>
            </Grid>
          </Box>
        </Section>
      )}

      <Section>
        <Box sx={{ p: 4 }}>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 2 }}>
            <Typography variant="textSM"
              fontWeight={600}>
              {t('trial.setupTitle', 'Trial setup')}
            </Typography>
            <Select
              size="small"
              value={trialStatus}
              onChange={(e) => setTrialStatus(e.target.value as TrialStatus)}
            >
              {(['Draft', 'Planned', 'Active', 'Paused', 'Completed', 'Approved', 'Rejected'] as TrialStatus[]).map((status) => (
                <MenuItem key={status}
                  value={status}>{t(`trial.status.${status}`, status)}</MenuItem>
              ))}
            </Select>
          </Stack>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.objective', 'Trial objective')}</Typography>
              <Typography variant="textSM">{profile.trial.objective}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.hypothesis', 'Trial hypothesis')}</Typography>
              <Typography variant="textSM">{profile.trial.hypothesis}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.owner', 'Trial owner')}</Typography>
              <Typography variant="textSM">{profile.trial.owner}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={2}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.startDate', 'Trial start date')}</Typography>
              <Typography variant="textSM">{profile.trial.startDate}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={2}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.endDate', 'Trial end date')}</Typography>
              <Typography variant="textSM">{profile.trial.endDate}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.participatingSites', 'Participating sites')}</Typography>
              <Stack direction="row"
                spacing={1}
                sx={{ mt: 0.5, flexWrap: 'wrap' }}>
                {profile.trial.trialSites.map((site) => (
                  <Chip key={site.storeNumber}
                    label={site.storeName}
                    size="small" />
                ))}
              </Stack>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.controlProducts', 'Control or comparison products')}</Typography>
              <Typography variant="textSM">{profile.trial.controlOrComparisonProducts.join(', ')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.customerGroups', 'Intended customer groups')}</Typography>
              <Typography variant="textSM">{profile.trial.intendedCustomerGroups.join(', ')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.mealPeriods', 'Intended meal periods')}</Typography>
              <Typography variant="textSM">{profile.trial.intendedMealPeriods.join(', ')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.menuCategories', 'Intended menu categories')}</Typography>
              <Typography variant="textSM">{profile.trial.intendedMenuCategories.join(', ')}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.seasonalAssociation', 'Seasonal or promotional association')}</Typography>
              <Typography variant="textSM">{profile.trial.seasonalOrPromotionalAssociation ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('trial.commercialTargetsTitle', 'Commercial targets')}
          </Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetSellingPrice', 'Target selling price')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.trial.commercialTargets.targetSellingPrice, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetFoodCost', 'Target food cost')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.trial.commercialTargets.targetFoodCost, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetFoodCostPercentage', 'Target food cost %')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.targetFoodCostPercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetGrossProfit', 'Target gross profit')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.trial.commercialTargets.targetGrossProfit, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetGrossMarginPercentage', 'Target gross margin %')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.targetGrossMarginPercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetContributionPerUnit', 'Target contribution per unit')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.trial.commercialTargets.targetContributionPerUnit, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetRevenue', 'Target revenue')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.trial.commercialTargets.targetRevenue, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.minimumViableWeeklySales', 'Minimum viable weekly sales')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.minimumViableWeeklySales}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.minimumViableDailySales', 'Minimum viable daily sales')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.minimumViableDailySales}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.maximumPromotionalDiscount', 'Maximum promotional discount')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.maximumPromotionalDiscountPercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetPromotionalUplift', 'Target promotional uplift %')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.targetPromotionalUpliftPercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetReturnOnPromotion', 'Target return on promotion')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.targetReturnOnPromotion.toFixed(1)}x</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.maximumIngredientCostVariance', 'Maximum acceptable ingredient cost variance')}</Typography>
              <Typography variant="textSM">{profile.trial.commercialTargets.maximumAcceptableIngredientCostVariancePercentage.toFixed(1)}%</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('trial.wasteTargetsTitle', 'Waste & sustainability targets')}
          </Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetTotalWaste', 'Target total waste %')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.targetTotalWastePercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.maximumAcceptableWaste', 'Maximum acceptable waste %')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.maximumAcceptableWastePercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetProductionWaste', 'Target production waste %')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.targetProductionWastePercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetServiceWaste', 'Target service waste %')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.targetServiceWastePercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetUnsoldWaste', 'Target unsold waste %')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.targetUnsoldWastePercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.targetPlateWaste', 'Target plate waste %')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.targetPlateWastePercentage.toFixed(1)}%</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.maximumWasteValue', 'Maximum waste value')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.trial.wasteTargets.maximumWasteValue, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.maximumWasteUnits', 'Maximum waste units')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.maximumWasteUnits}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.expectedShelfLife', 'Expected shelf life')}</Typography>
              <Typography variant="textSM">{t('trial.days', '{{count}} day(s)', { count: profile.trial.wasteTargets.expectedShelfLifeDays })}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.minimumRemainingShelfLife', 'Minimum remaining shelf life at delivery')}</Typography>
              <Typography variant="textSM">{t('trial.days', '{{count}} day(s)', { count: profile.trial.wasteTargets.minimumRemainingShelfLifeAtDeliveryDays })}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.wasteMeasurementUnit', 'Waste measurement unit')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.wasteMeasurementUnit}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={3}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.donationEligibility', 'Donation eligibility')}</Typography>
              <Chip
                label={profile.trial.wasteTargets.donationEligible ? t('trial.donationEligible', 'Eligible') : t('trial.donationNotEligible', 'Not eligible')}
                size="small"
                color={profile.trial.wasteTargets.donationEligible ? 'success' : 'default'}
              />
            </Grid>
            <Grid item
              xs={12}
              md={6}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.reuseOptions', 'Reuse or repurposing options')}</Typography>
              <Typography variant="textSM">{profile.trial.wasteTargets.reuseOrRepurposingOptions}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('trial.expectedDemand', 'Expected demand')}
          </Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.unitsPerWeek', 'Units per week')}</Typography>
              <Typography variant="textMD"
                fontWeight={600}>{profile.expectedDemand.unitsPerWeek}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.confidence', 'Confidence')}</Typography>
              <Chip
                label={profile.expectedDemand.confidence}
                size="small"
                color={profile.expectedDemand.confidence === 'Low' ? 'warning' : profile.expectedDemand.confidence === 'High' ? 'success' : 'default'}
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.basis', 'Basis')}</Typography>
              <Typography variant="textSM">{profile.expectedDemand.basis}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('trial.labourNeeded', 'Labour needed')}
          </Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.prepMinutesPerUnit', 'Prep minutes per unit')}</Typography>
              <Typography variant="textMD"
                fontWeight={600}>{profile.labourNeeded.prepMinutesPerUnit}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.skillLevel', 'Skill level')}</Typography>
              <Typography variant="textSM">{profile.labourNeeded.skillLevel}</Typography>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.notes', 'Notes')}</Typography>
              <Typography variant="textSM">{profile.labourNeeded.notes ?? '-'}</Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Typography variant="textSM"
            fontWeight={600}
            sx={{ mb: 2 }}>
            {t('trial.forecastPnl', 'Forecast P&L')}
          </Typography>
          <Grid container
            spacing={2}>
            <Grid item
              xs={6}
              md={2.4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.sellPrice', 'Sell price')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.forecastPnL.sellPrice, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={2.4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.foodCost', 'Food cost / unit')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.forecastPnL.foodCostPerUnit, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={2.4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.labourCost', 'Labour cost / unit')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.forecastPnL.labourCostPerUnit, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={2.4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.revenuePerWeek', 'Revenue / week')}</Typography>
              <Typography variant="textSM">{applyRoundAndFormat(profile.forecastPnL.revenuePerWeek, currencyCode)}</Typography>
            </Grid>
            <Grid item
              xs={6}
              md={2.4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.grossProfitMargin', 'Gross profit / margin')}</Typography>
              <Typography variant="textSM"
                fontWeight={600}>
                {applyRoundAndFormat(profile.forecastPnL.grossProfitPerWeek, currencyCode)} ({profile.forecastPnL.marginPercentage.toFixed(1)}%)
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 2 }}>
            <Typography variant="textSM"
              fontWeight={600}>
              {t('trial.trialSetup', 'Trial sites, dates & success criteria')}
            </Typography>
            <Chip
              label={profile.trial.outcome}
              size="small"
              color={profile.trial.outcome === 'Success' ? 'success' : profile.trial.outcome === 'Failed' ? 'error' : 'default'}
            />
          </Stack>
          <Grid container
            spacing={2}
            sx={{ mb: 3 }}>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.trialSites', 'Trial sites')}</Typography>
              <Stack direction="row"
                spacing={1}
                sx={{ mt: 1, flexWrap: 'wrap' }}>
                {profile.trial.trialSites.map((site) => (
                  <Chip key={site.storeNumber}
                    label={site.storeName}
                    size="small" />
                ))}
              </Stack>
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Typography variant="textXS"
                color="text.secondary">{t('trial.trialDates', 'Trial dates')}</Typography>
              <Typography variant="textSM">{profile.trial.startDate} - {profile.trial.endDate}</Typography>
            </Grid>
          </Grid>
          <Typography variant="textXS"
            color="text.secondary"
            sx={{ mb: 1 }}>{t('trial.successCriteria', 'Success criteria')}</Typography>
          <Stack spacing={0.5}
            sx={{ mb: 3 }}>
            {profile.trial.successCriteria.map((criterion) => (
              <Typography key={criterion}
                variant="textSM">{'\u2022'} {criterion}</Typography>
            ))}
          </Stack>

          <Divider sx={{ mb: 3 }} />

          <Stack direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}>
            <Stack direction="row"
              spacing={1}
              alignItems="center">
              <Typography variant="textSM"
                fontWeight={600}>{t('trial.approvalStep', 'Approve supplier/product/range')}</Typography>
              <Chip label={approved ? t('trial.approved', 'Approved') : t('trial.pendingApproval', 'Pending approval')}
                color={approved ? 'success' : 'warning'}
                size="small" />
            </Stack>
            {!approved && canApprove && (
              <Button variant="primary"
                size="md"
                onClick={() => setApproved(true)}>
                {t('trial.approveAction', 'Approve to run trial')}
              </Button>
            )}
            {!approved && !canApprove && (
              <Typography variant="textXS"
                color="text.secondary">
                {t('trial.requiresApprovalRole', 'Requires recipe approval permission.')}
              </Typography>
            )}
          </Stack>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 2 }}>
            <Typography variant="textSM"
              fontWeight={600}>
              {t('trial.recordResults', 'Monitor & review: sales, waste, margin, feedback and supplier performance')}
            </Typography>
            {resultsSaved && (
              <Chip label={t('trial.resultsSaved', 'Saved')}
                size="small"
                color="success" />
            )}
          </Stack>

          <Typography variant="textXS"
            color="text.secondary"
            sx={{ mb: 1 }}>{t('trial.successCriteriaMet', 'Which success criteria were met?')}</Typography>
          <Stack sx={{ mb: 3 }}>
            {profile.trial.successCriteria.map((criterion, index) => (
              <FormControlLabel
                key={criterion}
                control={
                  <Checkbox checked={criteriaMet[index] ?? false}
                    onChange={() => handleToggleCriterion(index)} />
                }
                label={<Typography variant="textSM">{criterion}</Typography>}
              />
            ))}
          </Stack>

          <Grid container
            spacing={2}
            sx={{ mb: 3 }}>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                label={t('trial.actualUnitsSoldPerWeek', 'Actual units sold / week')}
                type="number"
                fullWidth
                size="small"
                value={actualUnitsSoldPerWeek}
                onChange={(e) => { setActualUnitsSoldPerWeek(e.target.value); setResultsSaved(false); }}
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                label={t('trial.actualMarginPercentage', 'Actual gross margin %')}
                type="number"
                fullWidth
                size="small"
                value={actualMarginPercentage}
                onChange={(e) => { setActualMarginPercentage(e.target.value); setResultsSaved(false); }}
                helperText={marginVariance != null
                  ? t('trial.marginVsForecast', 'Forecast {{forecast}}% ({{sign}}{{variance}}pp)', {
                    forecast: profile.forecastPnL.marginPercentage.toFixed(1),
                    sign: marginVariance >= 0 ? '+' : '',
                    variance: marginVariance.toFixed(1),
                  })
                  : t('trial.marginVsForecastEmpty', 'Forecast {{forecast}}%', { forecast: profile.forecastPnL.marginPercentage.toFixed(1) })}
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <TextField
                label={t('trial.actualWastePercentage', 'Actual waste %')}
                type="number"
                fullWidth
                size="small"
                value={actualWastePercentage}
                onChange={(e) => { setActualWastePercentage(e.target.value); setResultsSaved(false); }}
              />
            </Grid>
            <Grid item
              xs={12}
              md={4}>
              <Select
                fullWidth
                size="small"
                value={outcome}
                onChange={(e) => { setOutcome(e.target.value as TrialOutcome); setResultsSaved(false); }}
              >
                <MenuItem value="Pending">{t('trial.outcomePending', 'Pending')}</MenuItem>
                <MenuItem value="Success">{t('trial.outcomeSuccess', 'Success')}</MenuItem>
                <MenuItem value="Failed">{t('trial.outcomeFailed', 'Failed')}</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <TextField
            label={t('trial.feedbackNotes', 'Customer/staff feedback')}
            multiline
            rows={2}
            fullWidth
            value={feedbackNotes}
            onChange={(e) => { setFeedbackNotes(e.target.value); setResultsSaved(false); }}
            sx={{ mb: 2 }}
          />

          <TextField
            label={t('trial.supplierPerformanceNotes', 'Supplier performance notes')}
            multiline
            rows={2}
            fullWidth
            value={supplierPerformanceNotes}
            onChange={(e) => { setSupplierPerformanceNotes(e.target.value); setResultsSaved(false); }}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 3 }} />

          <Stack direction="row"
            justifyContent="flex-end">
            <Button variant="primary"
              onClick={handleSaveResults}>
              {t('trial.saveResults', 'Save trial results')}
            </Button>
          </Stack>
        </Box>
      </Section>

      <Section>
        <Box sx={{ p: 4 }}>
          <Stack direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            sx={{ mb: 2 }}>
            <Typography variant="textSM"
              fontWeight={600}>
              {t('trial.decideTitle', 'Decide: stop, extend, adjust, or make permanent')}
            </Typography>
            {decisionSaved && (
              <Chip label={t('trial.decisionSaved', 'Saved')}
                size="small"
                color="success" />
            )}
          </Stack>

          <Grid container
            spacing={2}
            sx={{ mb: 3 }}>
            <Grid item
              xs={12}
              md={4}>
              <Select
                fullWidth
                size="small"
                displayEmpty
                value={decision}
                onChange={(e) => { setDecision(e.target.value as TrialDecision); setDecisionSaved(false); }}
              >
                <MenuItem value=""
                  disabled>{t('trial.selectDecision', 'Select a decision')}</MenuItem>
                <MenuItem value="Stop">{t('trial.decisionStop', 'Stop')}</MenuItem>
                <MenuItem value="Extend">{t('trial.decisionExtend', 'Extend')}</MenuItem>
                <MenuItem value="Adjust">{t('trial.decisionAdjust', 'Adjust')}</MenuItem>
                <MenuItem value="MakePermanent">{t('trial.decisionMakePermanent', 'Make permanent')}</MenuItem>
              </Select>
            </Grid>
          </Grid>

          <TextField
            label={t('trial.decisionNotes', 'Decision notes')}
            multiline
            rows={2}
            fullWidth
            value={decisionNotes}
            onChange={(e) => { setDecisionNotes(e.target.value); setDecisionSaved(false); }}
            sx={{ mb: 3 }}
          />

          <Divider sx={{ mb: 3 }} />

          <Stack direction="row"
            justifyContent="flex-end">
            <Button variant="primary"
              onClick={handleSaveDecision}
              disabled={decision === ''}>
              {t('trial.saveDecision', 'Save decision')}
            </Button>
          </Stack>
        </Box>
      </Section>

      {profile.requiresNewSupplier && (
        <Section>
          <Box sx={{ p: 4 }}>
            <Typography variant="textSM"
              fontWeight={600}
              sx={{ mb: 1 }}>
              {t('trial.newSupplierNeeded', 'New supplier needed')}
            </Typography>
            <Typography variant="textSM"
              color="text.secondary"
              sx={{ mb: 2 }}>
              {t('trial.newSupplierNeededDesc', 'This product needs {{supplier}} onboarded before the trial can start.', { supplier: profile.proposedSupplierName })}
            </Typography>
            {alreadyReferred ? (
              <Alert severity="success">
                {t('trial.referredSuccess', 'Referred to procurement - {{supplier}} now appears as a pending onboarding request on the Suppliers page.', { supplier: profile.proposedSupplierName })}
              </Alert>
            ) : (
              <Button variant="primary"
                onClick={handleReferToProcurement}>
                {t('trial.referToProcurement', 'Refer to Procurement')}
              </Button>
            )}
          </Box>
        </Section>
      )}
    </Stack>
  );
};

export default TrialTab;
